import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as teamsService from "../service/Teams"
import { TeamsDetailRead, UserDetailRead, UserTeamMappingUpdate } from "../api/generated"
import useShowError from "./useShowError"
import useShowToast from "../hooks/useShowToast"

const TEAMS = "teams"
const USER_TEAM_MAPPING = "userTeamMapping"

export const useGetTeams = () =>
  useQuery({
    queryKey: [TEAMS],
    queryFn: () => teamsService.getAllTeams(),
    placeholderData: [],
  })

export const useUpdateTeam = () => {
  const queryClient = useQueryClient()
  const showError = useShowError()

  return useMutation({
    mutationFn: ({
      id,
      name,
      teamLeadId,
      workqueueIds,
    }: {
      id: number
      name: string
      teamLeadId: number
      workqueueIds: number[]
    }) =>
      teamsService.updateTeam(id, {
        name,
        team_lead_id: teamLeadId,
        workqueue_ids: workqueueIds,
      }),
    onSuccess(response) {
      queryClient.setQueryData([TEAMS], (old: Array<TeamsDetailRead>) =>
        old.map((item) => (item.id === response.id ? response : item)),
      )
      queryClient.invalidateQueries({ queryKey: ["opco"] });
    },
    onError(error) {
      showError(`Failed to update team: ${error.message}`)
    },
  })
}

export const useDeleteTeam = () => {
  const queryClient = useQueryClient()
  const showError = useShowError()

  return useMutation({
    mutationFn: ({ id }: TeamsDetailRead) => teamsService.deleteTeam(id),
    onSuccess({ id }) {
      queryClient.setQueryData([TEAMS], (old: Array<TeamsDetailRead>) =>
        old.filter((item) => item.id !== id),
      )
    },
    onError(error) {
      showError(`Failed to delete team: ${error.message}`)
    },
  })
}

export const useCreateTeam = () => {
  const queryClient = useQueryClient()
  const showError = useShowError()
  const showToast = useShowToast();

  return useMutation({
    mutationFn: (team: TeamsDetailRead) =>
      teamsService.createTeam({
        name: team.name || "",
        team_lead_id: team.team_lead.id,
        workqueue_ids: team.workqueues.map((queue) => queue.id),
      }),
    onSuccess() {
      queryClient.invalidateQueries({queryKey: [USER_TEAM_MAPPING]});
      queryClient.invalidateQueries({queryKey: [TEAMS]});
      showToast("Team has been successfully created.")
    },
    onError(error) {
      showError(`Failed to create team: ${error.message}`)
    },
  })
}

export const useGetUserTeams = () => {
  return useQuery({
    queryKey: [USER_TEAM_MAPPING],
    queryFn: () => teamsService.getUserTeams(),
    placeholderData: [],
  })
}

export const useUpdateUserTeamMapping = () => {
  const queryClient = useQueryClient()
  const showError = useShowError()

  return useMutation<
    UserDetailRead, // response type
    Error, // error type
    { id: number; payload: UserTeamMappingUpdate } // variables type
  >({
    mutationFn: ({ id, payload }) =>
      teamsService.updateUserTeams(id, payload),
    onSuccess(response) {
      queryClient.setQueryData([USER_TEAM_MAPPING], (old: Array<UserDetailRead>) =>
        old.map((item) => (item.id === response?.id ? response : item)),
      )
    },
    onError(error) {
      showError(`Failed to update team: ${error.message}`)
    },
  })
}
